/* agmf-sale-card.js — the sale card, inside the feed.
 *
 * Dorian 2026-09-30: "when there is a sale active i would like to be able to
 * activate the countdown from control panel or media and have a card
 * reflecting the countdown of sale within the feed" + "not the selection i
 * meant the discount cart + add to cart button".
 *
 * So: the discounted price, a countdown to the end of the sale, and an Add to
 * cart of its own, repeating every N cards. It is the delivery card's shape
 * with different content, and it borrows that card's two best decisions.
 *
 * IT MIRRORS THE PRICE, IT DOES NOT STATE ONE. The buy column already renders
 * `.price-list--product` — <sale-price> and <compare-at-price> — and because
 * those carry `form="…"` the theme re-renders them on every variant change.
 * Cloning that gives the card the currency, the locale, the strikethrough and
 * the variant reactivity for free, and makes it impossible for the card to
 * disagree with the price the shopper is actually charged. A discount figure
 * typed into a config would be a second source of truth, and the one thing a
 * second source never copies is the next price change.
 *
 * TWO THINGS MUST BOTH BE TRUE, and they answer different questions:
 *   · the PANEL says a sale is on and when it ends — a campaign fact, the same
 *     sale across the store, and the only thing a human switches;
 *   · THIS PRODUCT is actually discounted — `<compare-at-price>` present and
 *     NOT `hidden` in the buy column (snippets/price-list.liquid). That is
 *     Shopify's own answer for the variant on screen.
 * A campaign without a discount on this product would be a card announcing a
 * saving the shopper is not getting; a discount without a campaign has no
 * deadline to count down to. Neither alone earns the card.
 *
 * THE COUNTDOWN IS NOT WRITTEN HERE. promo-timer.js already ticks, formats
 * "Nd Nh Nm Ns", removes itself at zero and calls back — it is Dorian's own
 * promo-card timer, and its header says it was built control-panel-ready for
 * exactly this. The card mounts it. If it is somehow absent the card still
 * renders its price and its button rather than throwing.
 *
 * IT LEAVES WHEN THE SALE DOES. The timer's onExpire hides EVERY instance of
 * the card, not just its own timer — otherwise the shopper is left with a
 * framed card still announcing a sale, next to an Add to cart, at a price
 * Shopify may already have put back up. A sale that has already ended when the
 * page loads never renders at all: that is checked before mounting, not left
 * to the first tick.
 *
 * THE CLOCK IS THE SHOPPER'S. promo-timer.js counts against Date.now(), so a
 * device with a wrong clock sees a wrong countdown. That is why this card
 * states no discount amount of its own: a skewed clock then gives a wrong
 * countdown beside a correct price, rather than two numbers contradicting each
 * other.
 *
 * THE BUTTON CLICKS THE REAL ONE, exactly as agmf-delivery-card.js does: the
 * chosen variant, the quantity, the US body-armour terms gate, the cart cap
 * and the free-gift row all behave as if the shopper had pressed the button in
 * the buy column, because they did. Mirroring that button's LABEL is what lets
 * this card say "Adding" and then "Added" without knowing those words exist.
 *
 * WHERE IT SITS is published with the feed as `saleCard {enabled, position,
 * every}`, the same shape the Ask and delivery cards read — and read LATE, on
 * first placement, because window.AGMF_FEED is only assigned once the feed
 * JSON has been fetched, which is after every deferred script has run. Reading
 * it at boot is what made the delivery card ignore its own setting for two
 * days (fixed in e7fe354; this card was never written that way).
 *
 * THE PAGE'S OWN "SALE ENDS IN" IS A SALE TOO (Dorian 2026-10-12: "create a
 * card in the feed that appears every <x> cards to remind about the sale",
 * then "it was the timer for sales"). The sale that is actually running was
 * announced from the Shopify admin, not the panel: a "SALE ENDS IN:" line and
 * a Sendtric countdown picture (an animated GIF the Sendtric server draws per
 * request) written into the product sections above the product. The panel
 * campaign (AGMF_SALE) is not wired, so this card never showed for it. With no
 * panel campaign, that block now IS the campaign: the card's head mirrors its
 * words and the very same countdown picture, so the deadline stays the
 * block's own and nothing here knows when the sale ends. The block gone =
 * no sale = no card. The discount gate below is unchanged.
 *
 * Isolation: own file, own stylesheet. It reads the DOM of three other
 * features and edits none. Kill: window.AGMF_SALE_CARD_OFF = true.
 */
(function () {
  'use strict';
  if (window.__agmfSaleCard || window.AGMF_SALE_CARD_OFF) return;
  window.__agmfSaleCard = true;

  // the buy column's price block: one per page, inside the product form's
  // column (snippets/product-info.liquid). `.price-list` WITHOUT the modifier
  // also appears on every recommendation tile, hence the modifier.
  var PRICE = '.price-list--product';
  // the same set agmf-delivery-card.js and agmf-buy-tail.js click, so all
  // three press the same button
  var ATC = 'product-form button[type="submit"], .product-form button[type="submit"], ' +
            'form[action*="/cart/add"] button[type="submit"], buy-buttons button';

  // "every <x> cards" over a long scroll: six reminders, then it stops
  var MAX_CARDS = 6;
  var started = false;
  var expired = false;
  var cards = [];                       // every card placed, so all stay in step

  function priceEl() { return document.querySelector(PRICE); }
  function atcSource() { return document.querySelector(ATC); }

  /* ---- is there a sale, and is THIS product in it? ------------------------ */

  /* The campaign. window.AGMF_SALE is what the control panel's envelope will
     fill in; until that is wired a query override lets the card be seen on the
     preview without one, the way `?storefront_env=` already works for the
     config layer. The override cannot fabricate a discount - the gate below
     still has to pass - so the worst it can do is show a deadline to whoever
     typed it. */
  function sale() {
    var s = null;
    try {
      var q = new URLSearchParams(location.search);
      var ends = q.get('agb_sale_ends');
      if (ends) s = { endsAt: ends, label: q.get('agb_sale_label') || undefined };
    } catch (e) {}
    if (!s && window.AGMF_SALE && window.AGMF_SALE.enabled !== false) s = window.AGMF_SALE;
    if (!s || !s.endsAt) return pageSale();
    var end = Date.parse(s.endsAt);
    // already over, or a date nothing could parse: no card, and no timer
    // mounted to discover it a second later
    if (!isFinite(end) || end - Date.now() <= 0) return null;
    return { endsAt: s.endsAt, label: s.label, end: end };
  }

  /* The sale block written into the product section from the Shopify admin
     (see the header): a countdown PICTURE and the words around it. Never
     inside the feed, where this card's own mirror of it lives.
     A picture counts when its address says /countdown/ (Sendtric and its
     kind name it so) OR when it sits in a short block whose words say
     "... ENDS IN" - because the picture is swapped from the admin: on
     2026-10-13 the Sendtric timer ran out, was commented out, and the block
     went back to a Shopify-hosted GIF (files/t42b3jxojh.gif) whose address
     says nothing of the kind. Matching on the address alone lost the sale
     card that morning. */
  var blockSeen = null;
  var ENDS_IN = /\bends?\s+in\b/i;
  function saleBlockImage(im) {
    if (im.closest('[data-agmf], .agmf-scard')) return false;
    if (/\/countdown\//.test(im.getAttribute('src') || '')) return true;
    var box = im.closest('center') || im.parentElement;
    var text = box ? (box.textContent || '').replace(/\s+/g, ' ').trim() : '';
    // a short announcement, not a paragraph of description that mentions it
    return text.length > 0 && text.length <= 80 && ENDS_IN.test(text);
  }
  function pageSale() {
    var img = blockSeen && blockSeen.isConnected ? blockSeen : null;
    if (!img) {
      var scope = document.querySelector('.shopify-section--main-product') ||
                  document.querySelector('main') || document.body;
      var all = scope ? scope.querySelectorAll('img') : [];
      for (var i = 0; i < all.length && !img; i++) {
        if (saleBlockImage(all[i])) img = all[i];
      }
      blockSeen = img;
    }
    if (!img) return null;
    var box = img.closest('center') || img.parentElement;
    var words = box ? (box.textContent || '').replace(/\s+/g, ' ').replace(/[:\s]+$/, '').trim() : '';
    return { page: true, label: words, img: img.getAttribute('src') };
  }

  /* Shopify's own answer, for the variant on screen. snippets/price-list.liquid
     renders <compare-at-price> with `hidden` unless compare_at_price > price,
     and re-renders it on variant change because of the `form` attribute - so
     this is live, not a page-load snapshot. */
  function discounted() {
    var p = priceEl();
    if (!p) return false;
    var was = p.querySelector('compare-at-price');
    return !!(was && !was.hasAttribute('hidden'));
  }

  /* ---- the variant on screen is sold out ---------------------------------
     Dorian 2026-10-13: "on the sale card it is written sold out when the
     variant that is selected is sold out but thats confusing" + "we need to
     allow to select other variant in that case cuz a sold out card is
     useless". The button mirrored the real one, so it said "Sold out" and did
     nothing. Now, while the variant the page holds is sold out, the card says
     WHICH variant is ("Black · Small · Sold out") and its button becomes the
     theme's own "Choose options", opening the page's variant sheet - the one
     the phone bar opens (agilite-media-flow.js initDrawer). APPLY there moves
     the buy column to the new variant, the price and the real button follow,
     and the card is an Add to cart again. Nothing on the product can be
     bought at all: the card goes, as there is nothing to choose.

     Availability comes from the section's own product JSON
     ([data-agmf-product-data], the sheet's source) for the variant the
     product bridge says the page holds - not from the real button being
     disabled, which it also is for the moment an add is in flight. */
  var pdata;                            // undefined = not read yet
  function productData() {
    if (pdata !== undefined) return pdata;
    pdata = null;
    var el = document.querySelector('[data-agmf-product-data]');
    try { pdata = el ? JSON.parse(el.textContent) : null; } catch (e) { pdata = null; }
    if (!pdata || !pdata.variants || !pdata.variants.length) pdata = null;
    return pdata;
  }

  // the variant the page holds, from this product's own list; null = unknown
  function heldVariant() {
    var pd = productData();
    var bridge = window.AGMF_PRODUCT_BRIDGE;
    if (!pd || !bridge || typeof bridge.getSelectedVariantId !== 'function') return null;
    var id = null;
    try { id = bridge.getSelectedVariantId(); } catch (e) {}
    if (id == null) return null;
    for (var i = 0; i < pd.variants.length; i++) {
      if (String(pd.variants[i].id) === String(id)) return pd.variants[i];
    }
    return null;
  }

  function anyAvailable() {
    var pd = productData();
    if (!pd) return true;               // unknown: do not hide on a guess
    for (var i = 0; i < pd.variants.length; i++) if (pd.variants[i].available) return true;
    return false;
  }

  // the theme's own words, in the shopper's language (sections/agilite-media-flow.liquid)
  function words() {
    var s = document.querySelector('[data-agmf]');
    var d = (s && s.dataset) || {};
    return { choose: d.saleChoose || 'Choose options', soldOut: d.saleSoldout || 'Sold out' };
  }

  // the page's variant sheet, opened the way the phone bar opens it; where a
  // page has no sheet, the buy column's own picker is brought into view
  function openChooser() {
    var opener = document.querySelector('[data-agmf-sticky-size]');
    if (opener) { opener.click(); return; }
    var picker = document.querySelector('.shopify-section--main-product variant-picker, ' +
                                        '.shopify-section--main-product .variant-picker');
    if (picker && picker.scrollIntoView) {
      try { picker.scrollIntoView({ block: 'center', behavior: 'smooth' }); }
      catch (e) { picker.scrollIntoView(); }
    }
  }

  /* ---- one card ---------------------------------------------------------- */

  function makeCard() {
    var card = document.createElement('article');
    card.className = 'agmf-card agmf-scard';
    card.setAttribute('data-agmf-sale-card', '');
    card.hidden = true;                 // until the two gates above pass

    var head = document.createElement('div');
    head.className = 'agmf-scard__head';
    card.appendChild(head);             // the timer is mounted in here

    var price = document.createElement('div');
    price.className = 'agmf-scard__price';
    // The clone is decoration: the real price block is in the buy column and
    // is the one a screen reader should read.
    price.setAttribute('aria-hidden', 'true');
    card.appendChild(price);

    // which variant is sold out, shown only while it is
    var note = document.createElement('p');
    note.className = 'agmf-scard__note';
    note.hidden = true;
    card.appendChild(note);

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'agmf-scard__atc';
    btn.setAttribute('data-agmf-sale-atc', '');
    btn.addEventListener('click', function (e) {
      // the card itself is clickable in the feed; this press is not that
      e.stopPropagation();
      if (btn.getAttribute('data-agmf-sale-mode') === 'choose') {
        // this card is in the shopper's hand now: it keeps its place in the
        // feed when the variant they pick brings another card into its gap
        card.setAttribute('data-agmf-hold', '');
        openChooser();
        return;
      }
      var s = atcSource();
      if (!s || s.disabled) return;
      s.click();
    });
    card.appendChild(btn);

    /* IT HAS TO SAY IT WAS THERE. The card carries no content key - and must
       not: agmf-seen.js collects `[data-agmf-ck]` and would enter the sale
       card into the SHOPPER's seen ledger, where only real content belongs.
       So the ordinary view tracker cannot count it, and it announces itself
       instead. This is the denominator a press of its Add to cart is read
       against. A card hidden for having no sale never intersects, so it is
       correctly never counted as shown. */
    if ('IntersectionObserver' in window) {
      var seenOnce = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting && entries[i].intersectionRatio >= 0.5) {
            seenOnce.disconnect();
            var host = card.parentElement;
            var slot = host ? Array.prototype.indexOf.call(host.children, card) : -1;
            try {
              document.dispatchEvent(new CustomEvent('agmf:sale', { detail: { kind: 'shown', slot: slot } }));
            } catch (e) { /* no CustomEvent: the card works, it just goes uncounted */ }
            return;
          }
        }
      }, { threshold: [0.5] });
      seenOnce.observe(card);
    }

    var rec = { card: card, head: head, price: price, note: note, btn: btn, timer: null };
    cards.push(rec);
    mountTimer(rec);
    paintOne(rec);
    return card;
  }

  function mountTimer(rec) {
    if (rec.timer || expired) return;
    var s = sale();
    if (s && s.page) { mirrorBlock(rec, s); return; }
    if (!s || !window.AGBPromoTimer) return;   // no deadline, or the timer did
                                               // not load: price + button only
    rec.timer = window.AGBPromoTimer.mount(rec.head, {
      endsAt: s.endsAt,
      label: s.label,                   // undefined leaves the timer's own word
      variant: 'plain',
      onExpire: retireAll
    });
  }

  // The page's sale block, mirrored: its words and the SAME countdown picture.
  // Same address = the browser's one copy of the animation, so the card and
  // the block tick together. It counts down nothing of its own, so there is
  // no expiry to watch; `timer` only marks the head as filled.
  function mirrorBlock(rec, s) {
    var box = document.createElement('div');
    box.className = 'agmf-scard__sale';
    if (s.label) {
      var words = document.createElement('span');
      words.className = 'agmf-scard__sale-label';
      words.textContent = s.label;
      box.appendChild(words);
    }
    var pic = document.createElement('img');
    pic.className = 'agmf-scard__sale-count';
    pic.alt = s.label || '';
    pic.decoding = 'async';
    pic.setAttribute('src', s.img);
    box.appendChild(pic);
    rec.head.appendChild(box);
    rec.timer = { page: true, stop: function () {} };
  }

  // THE SALE ENDED WITH THE PAGE OPEN. Every card goes, not only the timer
  // that fired - and once, however many cards are on screen.
  function retireAll() {
    if (expired) return;
    expired = true;
    for (var i = 0; i < cards.length; i++) {
      if (cards[i].timer && cards[i].timer.stop) cards[i].timer.stop();
      cards[i].card.hidden = true;
    }
  }

  function paintOne(rec) {
    var show = !expired && !!sale() && discounted();
    var held = show ? heldVariant() : null;
    var soldOut = !!(held && !held.available);
    // sold out with nothing else to choose: no card rather than a dead one
    if (soldOut && !anyAvailable()) show = false;
    rec.card.hidden = !show;
    if (!show) return;

    var p = priceEl();
    var html = p ? p.innerHTML : '';
    if (html && rec.price.innerHTML !== html) rec.price.innerHTML = html;

    if (soldOut) {
      var w = words();
      var what = (held.options || []).join(' · ');
      var line = (what ? what + ' · ' : '') + w.soldOut;
      if (rec.note.textContent !== line) rec.note.textContent = line;
      rec.note.hidden = false;
      rec.btn.setAttribute('data-agmf-sale-mode', 'choose');
      if (rec.btn.textContent !== w.choose) rec.btn.textContent = w.choose;
      rec.btn.disabled = false;
      rec.btn.setAttribute('aria-disabled', 'false');
      mountTimer(rec);
      return;
    }
    rec.note.hidden = true;
    rec.btn.removeAttribute('data-agmf-sale-mode');

    var s = atcSource();
    if (s) {
      var label = (s.textContent || '').replace(/\s+/g, ' ').trim();
      if (label && rec.btn.textContent !== label) rec.btn.textContent = label;
      var off = !!s.disabled || s.getAttribute('aria-disabled') === 'true';
      rec.btn.disabled = off;
      rec.btn.setAttribute('aria-disabled', String(off));
    }
    mountTimer(rec);                    // a card built before the sale was known
  }

  function paintAll() { for (var i = 0; i < cards.length; i++) paintOne(cards[i]); }

  /* ---- keep the copies in step ------------------------------------------- */

  var priceWatched = null, priceObs = null, atcWatched = null, atcObs = null;

  function watch() {
    var p = priceEl();
    if (p && p !== priceWatched) {
      if (priceObs) priceObs.disconnect();
      priceWatched = p;
      // attributes: `hidden` coming and going on <compare-at-price> IS the
      // discount changing under a variant switch
      priceObs = new MutationObserver(paintAll);
      priceObs.observe(p, { childList: true, subtree: true, characterData: true, attributes: true });
    }
    var s = atcSource();
    if (s && s !== atcWatched) {
      if (atcObs) atcObs.disconnect();
      atcWatched = s;
      atcObs = new MutationObserver(paintAll);
      atcObs.observe(s, { childList: true, subtree: true, characterData: true,
                         attributes: true, attributeFilter: ['disabled', 'aria-disabled'] });
    }
    paintAll();
  }

  /* ---- where it sits in the feed ----------------------------------------- */

  function start(section) {
    if (started) return;

    var feed = section.querySelector('[data-agmf-feed]');
    if (!feed) return;
    if (!sale()) return;                // no campaign: nothing to count down to
    var runtime = window.AGMF_CARD_RUNTIME;
    if (!runtime) return;
    started = true;

    /* PLACED BY THE SHARED RUNTIME, like the Ask and delivery cards (Dorian
       2026-10-12: the sale card sat right after the free-shipping card, "its
       too similar ... so they won't be one next to the other"). It used to
       place itself, outside the runtime's view. The card still goes to the
       gap its setting names; the runtime lets a gap SHOW one card, so where
       an earlier card in that gap is showing (free shipping after groups 1
       and 7 today) this one steps aside there (data-agmf-yield) and comes
       back if that card hides. It also reads the settings late
       (window.AGMF_FEED exists only once the feed JSON is fetched) and keeps
       a deliberate position 0.

       Defaults stand in when the key is absent, and they are Agilite Media's
       own ("Sale countdown card": after the first group, then every second
       one). The cards are SIBLINGS of the cycles, never children: recycle()
       calls replaceChildren() on a cycle three viewports away and rebuilds it
       from the feed, which knows nothing about this card. */
    runtime.mount({
      id: 'sale-feed',
      section: section,
      feed: feed,
      recipe: function () { return (window.AGMF_FEED && window.AGMF_FEED.saleCard) || {}; },
      defaultPosition: 1,
      defaultEvery: 2,
      maxCards: MAX_CARDS,
      create: makeCard,
      onUpdate: watch                   // the buy column can be re-rendered under us
    });
    watch();

    // the buy column's button and price are re-rendered on variant change
    document.addEventListener('variant:change', function () { setTimeout(watch, 0); });
  }

  /* WAIT UNTIL THE FEED HAS PUBLISHED. `[data-agmf]` is static Liquid markup,
     so booting on it runs during defer execution - before the feed has been
     fetched and window.AGMF_FEED assigned - and the placement settings are
     silently lost. The first `.agmf-cycle` cannot exist until the feed has
     been parsed and built. Same boot as the Ask card. */
  function boot() {
    var section = document.querySelector('[data-agmf]');
    if (!section) return false;
    if (section.querySelector('.agmf-cycle')) { start(section); return true; }
    var failsafe = setTimeout(function () { obs.disconnect(); start(section); }, 8000);
    var obs = new MutationObserver(function () {
      if (!section.querySelector('.agmf-cycle')) return;
      obs.disconnect();
      clearTimeout(failsafe);
      start(section);
    });
    obs.observe(section, { childList: true, subtree: true });
    return true;
  }

  if (!boot()) {
    var tries = 0;
    var iv = setInterval(function () {
      if (boot() || ++tries > 40) clearInterval(iv);
    }, 250);
  }
})();
